import { BaseInputProps } from '@paytrace/pt-skel/Component'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { InlineValidationInputText } from './InlineValidationInputText'
import { ConsumerValueMapper, RowErrorHandler, RowFieldDefinition, RowInputChangeHandler } from './Types'

export interface RowInputTextProp extends BaseInputProps {
  fieldDefinition: RowFieldDefinition
  onInputChange: RowInputChangeHandler<any>
  rowKey: string | number
  prefixIcon?: string | React.JSX.Element
  suffixIcon?: string | React.JSX.Element
  onRowError?: RowErrorHandler
  onRowInputFocus?: (rowKey: string | number, fieldName: string, value: string) => void
  runValidationNow?: boolean
}

/**
 * Maps a display value into a consumable state value using the provided mapper.
 */
export const getConsumableValue = (value: any, consumerValueMapper?: ConsumerValueMapper): any => {
  if (!consumerValueMapper) return value
  return consumerValueMapper(value)
}

/**
 * Custom RowInputText component that synchronizes parent state on every keystroke
 * while deferring display formatting until blur.
 */
export default function RowInputText (props: RowInputTextProp): React.JSX.Element {
  const { value, fieldDefinition, className, rowKey, prefixIcon, suffixIcon, runValidationNow = false, ...callbacks } = props
  const { onInputChange, onRowInputFocus, onRowError, ...rest } = callbacks
  const { name, maxLength, minLength, pattern, errorMessage } = fieldDefinition

  // Ref to track whether the user is actively focused on this input field
  const isFocusedRef = useRef(false)

  // Helper function to format display values (e.g., 5 -> "5.00")
  const formatValue = useCallback((val: any) => {
    return fieldDefinition.dataType?.formatter ? fieldDefinition.dataType.formatter(val) : val
  }, [fieldDefinition.dataType])

  // Helper function to map display values to consumable parent values (e.g., "5.00" -> 5)
  const getMappedConsumableValue = useCallback((val: any) => {
    return getConsumableValue(val, fieldDefinition.dataType?.consumerValueMapper)
  }, [fieldDefinition.dataType?.consumerValueMapper])

  // Initialize local state with formatted prop value
  const [localValue, setLocalValue] = useState(() => formatValue(value))

  // Track the last consumable value that the component has emitted
  const lastEmittedConsumableValRef = useRef<any>(getConsumableValue(formatValue(value)))

  // Sync from parent ONLY when input is not focused (e.g., initial load or external form reset)
  useEffect(() => {
    if (isFocusedRef.current) {
      return // Ignore prop re-renders while the input is on focus and the user is actively typing
    }
    setLocalValue(formatValue(value))
  }, [value, formatValue])

  // Handle focus: Mark ref as focused
  const handleOnFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = true
    if (onRowInputFocus) {
      onRowInputFocus(rowKey, fieldDefinition.name, e.target.value)
    }
  }, [fieldDefinition.name, onRowInputFocus, rowKey])

  const triggerOnChange = useCallback((consumableValue: any) => {
    lastEmittedConsumableValRef.current = consumableValue // updating the ref right before triggering onChange event
    onInputChange(rowKey, name, consumableValue)
  }, [name, onInputChange, rowKey])

  // Handle typing: Immediately sync parent on every character while preserving raw typed text locally
  const handleTextInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: inputValue } = event.target

    // Store raw text locally so characters like "." or trailing zeros are not removed
    setLocalValue(inputValue)

    // Instantly notify parent of consumable value on every keystroke
    triggerOnChange(getMappedConsumableValue(inputValue))
  }, [getMappedConsumableValue, triggerOnChange])

  // Handle blur: Format input visually and emit final formatted value to parent
  const handleOnBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false

    const currentValue = e.target.value
    const formattedValue = formatValue(currentValue)
    const consumableValue = getMappedConsumableValue(formattedValue)

    // Set formatted text in local display (e.g., "5." becomes "5.00")
    setLocalValue(formattedValue)

    // Ensure parent state matches final formatted consumable value
    const hasConsumableValueChanged = consumableValue !== lastEmittedConsumableValRef.current
    if (hasConsumableValueChanged) {
      triggerOnChange(consumableValue)
    }
  }, [formatValue, getMappedConsumableValue, triggerOnChange])

  const handleValidationError = useCallback((message: any, isVisible: boolean) => {
    if (onRowError) {
      onRowError(rowKey, name, message || null, isVisible)
    }
  }, [name, onRowError, rowKey])

  return (
    <div>
      <InlineValidationInputText
        className={className || 'row-input'}
        name={name}
        id={name}
        value={localValue}
        onChange={handleTextInputChange}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        maxLength={maxLength}
        minLength={minLength}
        pattern={pattern}
        dataRowKey={rowKey}
        removeCustomInputWrap={true}
        prefixInput={prefixIcon}
        suffixInput={suffixIcon}
        showInlineError={false}
        onValidationError={handleValidationError}
        fieldErrorMessage={errorMessage}
        runValidationNow={runValidationNow}
        {...rest}
      />
    </div>
  )
}
